import React, { useState } from 'react';
import { Button } from './ui/button';
import { UploadCloud, CheckCircle, AlertCircle } from 'lucide-react';

interface ImageUploadProps {
  onImageUpload: (imageUrl: string) => void;
  className?: string;
}

export function ImageUpload({ onImageUpload, className = '' }: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset states
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/images/upload', {
        method: 'POST',
        body: formData,
      });

      // Check if the response is ok
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to upload image';
        try {
          // Try to parse as JSON, but handle text responses too
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If parsing fails, use the raw text
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Get the response text first
      const responseText = await response.text();
      
      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        // If parsing fails, the response is not JSON
        throw new Error('Server returned an invalid response format');
      }

      setUploadSuccess(true);
      onImageUpload(data.imageUrl);
    } catch (error) {
      setUploadError((error as Error).message || 'An unknown error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={`flex flex-col items-center gap-4 ${className}`}>
      <label 
        htmlFor="image-upload" 
        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer border-gray-300 hover:border-gray-400"
      >
        {!isUploading && !uploadSuccess ? (
          <>
            <UploadCloud className="w-8 h-8 mb-2 text-gray-500" />
            <p className="text-sm text-gray-500">Click to upload image</p>
          </>
        ) : uploadSuccess ? (
          <>
            <CheckCircle className="w-8 h-8 mb-2 text-green-500" />
            <p className="text-sm text-green-500">Upload successful!</p>
          </>
        ) : (
          <>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
            </div>
            <p className="text-sm text-gray-500">Uploading... {uploadProgress}%</p>
          </>
        )}
        <input 
          id="image-upload" 
          type="file" 
          accept="image/*" 
          className="hidden" 
          onChange={handleFileChange}
          disabled={isUploading}
        />
      </label>
      
      {uploadError && (
        <div className="flex items-center text-red-500 text-sm">
          <AlertCircle className="w-4 h-4 mr-1" />
          {uploadError}
        </div>
      )}
    </div>
  );
}
